// Supply PO Inbox strings — EN + TR.
//
// Wave-4 Day 4. Wave-13 Phase 1B SupplyPurchaseOrderInbox.tsx was a pure-EN
// outlier — Mehmet TR persona seçince EN kalıyordu (consistency violation).
// Bu dict bilingual switch sağlar.
//
// Surface: features/supply/SupplyPurchaseOrderInbox.tsx (369 LOC; persona-
// scoped PO list per D-5 Ring 2; status-conditional actions Acknowledge +
// Mark Shipped; Mark Shipped dialog with tracking + ETA + note).
//
// Status enum source: mocks/data/inventory/purchase-orders POStatus (9 enum
// values: draft, sent, acknowledged, in_transit, partial_received, received,
// disputed, closed, cancelled). statusBadges covers the actual enum.
//
// Wave-1 helpers consume:
// - lib/datetime.ts (formatDate) — already used; dict adds locale-aware
//   labels only

import type { LocalizedDict } from '../index'

type SupplyPoInboxStrings = {
  pageTitleFormat: (vendorName: string) => string
  descriptionFormat: (vendorName: string, vendorType: string) => string
  loading: string
  emptyState: {
    descriptionFormat: (vendorName: string) => string
  }
  table: {
    columnHeaders: {
      poNumber: string
      createdAt: string
      totalQty: string
      status: string
      actions: string
    }
  }
  actions: {
    acknowledge: string
    markShipped: string
    openDispute: string
    upload: string
    uploadTitle: string
    openDisputeTitle: string
  }
  shipDialog: {
    titleFormat: (poNumber: string) => string
    description: string
    trackingLabel: string
    trackingPlaceholder: string
    etaLabel: string
    noteLabel: string
    notePlaceholder: string
    cancel: string
    confirm: string
  }
  toasts: {
    acknowledgedAdminTitleFormat: (
      vendorName: string,
      poNumber: string,
    ) => string
    acknowledgedAdminDescFormat: (qty: number, terms: string) => string
    acknowledgedSupplyTitleFormat: (poNumber: string) => string
    acknowledgedSupplyDesc: string
    acknowledgeFailed: string
    shippedAdminTitleFormat: (
      vendorName: string,
      poNumber: string,
    ) => string
    shippedAdminDescFormat: (tracking: string, eta: string) => string
    shippedSupplyTitleFormat: (poNumber: string) => string
    shipFailed: string
  }
  emDash: string
}

export const SUPPLY_PO_INBOX_STRINGS: LocalizedDict<SupplyPoInboxStrings> = {
  en: {
    pageTitleFormat: (vendorName) => `PO Inbox — ${vendorName}`,
    descriptionFormat: (vendorName, vendorType) =>
      `Purchase orders directed to ${vendorName} (${vendorType}). Persona switch in the topbar refreshes this inbox automatically.`,
    loading: 'Loading purchase orders…',
    emptyState: {
      descriptionFormat: (vendorName) =>
        `No purchase orders for ${vendorName} yet.`,
    },
    table: {
      columnHeaders: {
        poNumber: 'PO#',
        createdAt: 'Created',
        totalQty: 'Total qty',
        status: 'Status',
        actions: 'Actions',
      },
    },
    actions: {
      acknowledge: 'Acknowledge',
      markShipped: 'Mark Shipped',
      openDispute: 'Open Dispute',
      upload: 'Upload',
      uploadTitle: 'Drive upload V1.5+ — invoice / shipping label',
      openDisputeTitle: 'Supply-side dispute view lands wave-13-day-8 candidate',
    },
    shipDialog: {
      titleFormat: (poNumber) => `Mark ${poNumber} as shipped`,
      description:
        'Provide carrier tracking number and ETA. PEXIFY warehouse will be notified.',
      trackingLabel: 'Tracking number',
      trackingPlaceholder: 'DHL-123-456',
      etaLabel: 'ETA',
      noteLabel: 'Note (optional)',
      notePlaceholder: 'e.g. Pallet 1/2 today, 2/2 tomorrow.',
      cancel: 'Cancel',
      confirm: 'Confirm shipment',
    },
    toasts: {
      acknowledgedAdminTitleFormat: (vendorName, poNumber) =>
        `${vendorName} acknowledged ${poNumber}`,
      acknowledgedAdminDescFormat: (qty, terms) =>
        `${qty} units · ${terms}`,
      acknowledgedSupplyTitleFormat: (poNumber) => `${poNumber} acknowledged`,
      acknowledgedSupplyDesc: 'PEXIFY is notified.',
      acknowledgeFailed: 'Acknowledge failed',
      shippedAdminTitleFormat: (vendorName, poNumber) =>
        `${vendorName} shipped ${poNumber}`,
      shippedAdminDescFormat: (tracking, eta) =>
        `${tracking} · ETA ${eta}`,
      shippedSupplyTitleFormat: (poNumber) => `${poNumber} marked shipped`,
      shipFailed: 'Ship failed',
    },
    emDash: '—',
  },
  tr: {
    pageTitleFormat: (vendorName) => `PO Gelen Kutusu — ${vendorName}`,
    descriptionFormat: (vendorName, vendorType) =>
      `${vendorName} (${vendorType}) için gelen satınalma siparişleri. Topbar'daki persona değiştirici bu kutuyu otomatik yeniler.`,
    loading: 'Satınalma siparişleri yükleniyor…',
    emptyState: {
      descriptionFormat: (vendorName) =>
        `${vendorName} için henüz satınalma siparişi yok.`,
    },
    table: {
      columnHeaders: {
        poNumber: 'PO#',
        createdAt: 'Oluşturulma',
        totalQty: 'Toplam adet',
        status: 'Durum',
        actions: 'İşlemler',
      },
    },
    actions: {
      acknowledge: 'Onayla',
      markShipped: 'Kargolandı',
      openDispute: 'İtiraz aç',
      upload: 'Yükle',
      uploadTitle: 'Drive yükleme V1.5+ — fatura / kargo etiketi',
      openDisputeTitle:
        'Tedarik taraflı itiraz ekranı wave-13-day-8 aday',
    },
    shipDialog: {
      titleFormat: (poNumber) => `${poNumber} kargolandı işaretle`,
      description:
        'Kargo takip numarası ve tahmini teslim tarihi gir. PEXIFY deposu bilgilendirilir.',
      trackingLabel: 'Takip numarası',
      trackingPlaceholder: 'DHL-123-456',
      etaLabel: 'Tahmini teslim',
      noteLabel: 'Not (opsiyonel)',
      notePlaceholder: 'Örn: Palet 1/2 bugün, 2/2 yarın.',
      cancel: 'Vazgeç',
      confirm: 'Kargoyu onayla',
    },
    toasts: {
      acknowledgedAdminTitleFormat: (vendorName, poNumber) =>
        `${vendorName} ${poNumber} siparişini onayladı`,
      acknowledgedAdminDescFormat: (qty, terms) =>
        `${qty} adet · ${terms}`,
      acknowledgedSupplyTitleFormat: (poNumber) => `${poNumber} onaylandı`,
      acknowledgedSupplyDesc: 'PEXIFY bilgilendirildi.',
      acknowledgeFailed: 'Onaylama başarısız',
      shippedAdminTitleFormat: (vendorName, poNumber) =>
        `${vendorName} ${poNumber} siparişini kargoya verdi`,
      shippedAdminDescFormat: (tracking, eta) =>
        `${tracking} · Tahmini teslim ${eta}`,
      shippedSupplyTitleFormat: (poNumber) => `${poNumber} kargolandı`,
      shipFailed: 'Kargo işlemi başarısız',
    },
    emDash: '—',
  },
}
